/**
 * The rich text stored in reports: a ProseMirror (TipTap) JSON document limited to an allowlist of
 * nodes and marks. Parsing drops unknown attributes, links must be http(s) or mailto, and the
 * JSON of one section is at most `REPORT_SECTION_MAX_BYTES`. No HTML is ever stored.
 */
import { REPORT_SECTION_MAX_BYTES } from '@mai-team-amusement/core';
import { z } from 'zod';
const MAX_TEXT_LENGTH = 20_000;
const MAX_HREF_LENGTH = 2048;
const MAX_ATTRIBUTE_LENGTH = 100;
const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);
export function isSafeHref(href) {
    try {
        return SAFE_PROTOCOLS.has(new URL(href).protocol);
    }
    catch {
        return false;
    }
}
const zAttribute = z.string().max(MAX_ATTRIBUTE_LENGTH).nullable().optional();
const zMark = z.discriminatedUnion('type', [
    z.object({ type: z.enum(['bold', 'italic', 'underline', 'strike']) }),
    z.object({
        type: z.literal('link'),
        attrs: z.object({
            href: z.string().max(MAX_HREF_LENGTH).refine(isSafeHref, 'only http(s) and mailto links'),
            target: zAttribute,
            rel: zAttribute,
        }),
    }),
]);
const zInline = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('text'),
        text: z.string().min(1).max(MAX_TEXT_LENGTH),
        marks: z.array(zMark).optional(),
    }),
    z.object({ type: z.literal('hardBreak') }),
]);
const zListItem = z.lazy(() => z.object({ type: z.literal('listItem'), content: z.array(zBlock) }));
const zBlock = z.lazy(() => z.discriminatedUnion('type', [
    z.object({ type: z.literal('paragraph'), content: z.array(zInline).optional() }),
    z.object({ type: z.literal('bulletList'), content: z.array(zListItem) }),
    z.object({
        type: z.literal('orderedList'),
        attrs: z
            .object({
            start: z.number().int().min(0).optional(),
            type: zAttribute,
        })
            .optional(),
        content: z.array(zListItem),
    }),
]));
const encoder = new TextEncoder();
/** UTF-8 size of the stored JSON. */
export const richTextBytes = (doc) => encoder.encode(JSON.stringify(doc)).length;
export const zRichText = z
    .object({ type: z.literal('doc'), content: z.array(zBlock).optional() })
    .refine((doc) => richTextBytes(doc) <= REPORT_SECTION_MAX_BYTES, 'section too large');
/** The empty document, as stored. */
export const EMPTY_RICH_TEXT = { type: 'doc', content: [] };
function hasText(nodes) {
    return (nodes ?? []).some((node) => {
        switch (node.type) {
            case 'text':
                return node.text.trim() !== '';
            case 'hardBreak':
                return false;
            case 'paragraph':
            case 'bulletList':
            case 'orderedList':
            case 'listItem':
                return hasText(node.content);
        }
    });
}
export function isEmptyRichText(doc) {
    return !hasText(doc.content);
}
/** Empty documents in any shape become `EMPTY_RICH_TEXT`, so equal content compares equal. */
export function normalizeRichText(doc) {
    return isEmptyRichText(doc) ? EMPTY_RICH_TEXT : doc;
}
export function sameRichText(a, b) {
    return JSON.stringify(normalizeRichText(a)) === JSON.stringify(normalizeRichText(b));
}
/** Plain text of a document (previews, search). */
export function richTextToPlain(doc) {
    const lines = [];
    const walk = (blocks) => {
        for (const block of blocks ?? []) {
            if (block.type === 'paragraph') {
                lines.push((block.content ?? [])
                    .map((inline) => (inline.type === 'text' ? inline.text : '\n'))
                    .join(''));
            }
            else {
                for (const item of block.content) {
                    walk(item.content);
                }
            }
        }
    };
    walk(doc.content);
    return lines.join('\n').trim();
}
//# sourceMappingURL=rich-text.js.map